import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useTriggerJob } from '../hooks/useTriggerJob';
import { triggerParamsSchema, type TriggerParamsForm } from '../schema/schedulerMonitor.schema';
import type { ApiError } from '../types/schedulerMonitor';

interface TriggerJobDialogProps {
  jobName: string;
  onSuccess: (executionId: number | null) => void;
  onClose: () => void;
}

/**
 * TriggerJobDialog — 배치 수동 트리거 확인 dialog.
 * role=dialog, aria-modal=true, ESC 닫기, focus trap.
 */
export function TriggerJobDialog({
  jobName,
  onSuccess,
  onClose,
}: TriggerJobDialogProps) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const triggerMutation = useTriggerJob();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TriggerParamsForm>({
    resolver: zodResolver(triggerParamsSchema),
    defaultValues: { runDate: '' },
  });

  // ESC 닫기
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // 취소 버튼 초기 포커스
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  const onSubmit = (form: TriggerParamsForm) => {
    const params: Record<string, string> = {};
    if (form.runDate) params['runDate'] = form.runDate;

    triggerMutation.mutate(
      { jobName, params: Object.keys(params).length > 0 ? params : undefined },
      {
        onSuccess: (data) => {
          onSuccess(data.executionId ?? null);
        },
      }
    );
  };

  const triggerError = triggerMutation.error as ApiError | undefined;
  const is409 = triggerError?.code === 'SCHED-409-001';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="trigger-dialog-title"
      aria-describedby="trigger-dialog-desc"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: 420,
          maxWidth: '90vw',
          boxShadow: 'var(--shadow-xl)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 제목 */}
        <h2
          id="trigger-dialog-title"
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {t('schedulerMonitor.trigger.dialog.title')}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* 작업명 */}
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                fontWeight: 600,
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('schedulerMonitor.trigger.dialog.jobName')}
            </label>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
              }}
            >
              {jobName}
            </p>
          </div>

          {/* runDate 파라미터 */}
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              htmlFor="trigger-run-date"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-secondary)',
                fontWeight: 600,
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('schedulerMonitor.trigger.dialog.params')}
            </label>
            <label
              htmlFor="trigger-run-date"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('schedulerMonitor.trigger.dialog.runDate')}
            </label>
            <input
              id="trigger-run-date"
              type="text"
              {...register('runDate')}
              placeholder={t('schedulerMonitor.trigger.dialog.runDatePlaceholder')}
              aria-invalid={!!errors.runDate}
              aria-describedby={errors.runDate ? 'trigger-run-date-error' : undefined}
              style={{
                width: '100%',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `1px solid ${errors.runDate ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-sans)',
                boxSizing: 'border-box',
              }}
            />
            {errors.runDate && (
              <p
                id="trigger-run-date-error"
                role="alert"
                style={{
                  margin: 'var(--spacing-1) 0 0',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                }}
              >
                {errors.runDate.message}
              </p>
            )}
          </div>

          {/* 경고 안내 */}
          <div
            id="trigger-dialog-desc"
            style={{
              padding: 'var(--spacing-3)',
              background: 'var(--color-surface-muted)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-4)',
              borderLeft: '3px solid var(--color-warning)',
            }}
          >
            {t('schedulerMonitor.trigger.dialog.warn')}
          </div>

          {/* 409 중복 오류 인라인 표시 */}
          {is409 && (
            <div
              role="alert"
              aria-live="polite"
              style={{
                padding: 'var(--spacing-3)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginBottom: 'var(--spacing-4)',
                borderLeft: '3px solid var(--color-danger)',
              }}
            >
              {t('schedulerMonitor.error.SCHED-409-001')}
            </div>
          )}

          {/* 일반 오류 */}
          {triggerError && !is409 && (
            <div
              role="alert"
              aria-live="polite"
              style={{
                padding: 'var(--spacing-3)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger)',
                marginBottom: 'var(--spacing-4)',
                borderLeft: '3px solid var(--color-danger)',
              }}
            >
              {t(`schedulerMonitor.error.${triggerError.code}`, {
                defaultValue: triggerError.message ?? t('schedulerMonitor.error.network'),
              })}
            </div>
          )}

          {/* 버튼 영역 */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
            <button
              ref={cancelRef}
              type="button"
              onClick={onClose}
              disabled={triggerMutation.isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: triggerMutation.isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('schedulerMonitor.trigger.dialog.cancel')}
            </button>
            <button
              type="submit"
              disabled={triggerMutation.isPending}
              aria-describedby={triggerMutation.isPending ? 'trigger-pending' : undefined}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: triggerMutation.isPending
                  ? 'var(--color-surface-muted)'
                  : 'var(--color-brand-primary)',
                color: triggerMutation.isPending
                  ? 'var(--color-text-muted)'
                  : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: triggerMutation.isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: triggerMutation.isPending ? 0.7 : 1,
                minWidth: 72,
              }}
            >
              {triggerMutation.isPending ? '...' : t('schedulerMonitor.trigger.dialog.confirm')}
            </button>
            {triggerMutation.isPending && (
              <span id="trigger-pending" style={{ display: 'none' }}>
                실행 중
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
